import { observer } from "mobx-react";
import React, { useState } from "react";
import {
  Card,
  Group,
  Text,
  Image,
  Button,
  Progress,
  Loader,
  Center,
  Alert,
  Stack,
  Tooltip,
} from "@mantine/core";
import { IconEdit, IconTrash, IconApps } from "@tabler/icons-react";
import { Cubby } from "../../stores/Cubby";
import { NostrConnection } from "../../stores/NostrConnection";
import { abbreviateNumber } from "../../helpers/abbreviate";
import { MODALS } from "../../helpers/constants";
import ConnectionStatusBadge from "./connectionStatusBadge";

const ConnectionCard = observer(
  ({ connection, cubby }: { connection: NostrConnection; cubby: Cubby }) => {
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

    const displayName = connection.name || connection.uuid.slice(0, 6);
    const hasBudget = connection.budget_amount_sats !== null;
    const budgetLeft = connection.total_amount_left_in_budget;
    const budgetUsed = hasBudget
      ? ((connection.budget_amount_sats - budgetLeft) /
          connection.budget_amount_sats) *
        100
      : 0;

    const formatBudget = (value: number) => abbreviateNumber(value, 1);

    const handleDelete = () => {
      cubby.cubbyFastInfo("delete_nostr_connection", {
        nostrConnectionUuid: connection.uuid,
      });
      connection.setStagedForDeletion(true);
      cubby.selectNostrConnection(undefined);
      cubby.setModal(MODALS.CONNECT_APPS);
    };

    const shouldShowDeleteButton = () => {
      if (connection.security_level_name === "send_and_receive") {
        return true;
      }
      if (cubby.hasMoreThanOneReceiveOnlyNostrConnection) {
        return true;
      }
      return false;
    };

    if (connection.isStagedForDeletion) {
      return (
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Center>
            <Loader size="md" />
          </Center>
        </Card>
      );
    }

    return (
      <Card shadow="sm" padding="md" radius="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <Image
              src="https://daxwktpz4hkwd.cloudfront.net/nwc_logos/nwc-square-color-two.png"
              alt="NWC Logo"
              w={20}
              h={20}
              fit="contain"
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <Text span fw={700} lh={1.25} style={{ minWidth: 0 }}>
              {displayName}
            </Text>
          </Group>

          {!isConfirmingDelete && (
            <>
              <Group gap="xs">
                <Tooltip
                  multiline
                  w={300}
                  disabled={connection.name !== "My Send & Receive Connection"}
                  withArrow
                  transitionProps={{ duration: 200 }}
                  label={
                    "To get the Connection Code, click the 🖉 Edit button and give this connection a unique name you will remember. "
                  }
                >
                  <Button
                    size="xs"
                    variant="filled"
                    color="green"
                    disabled={
                      connection.security_level_name === "send_and_receive" &&
                      connection.name == "My Send & Receive Connection"
                    }
                    leftSection={<IconApps size={14} />}
                    onClick={() => {
                      cubby.selectNostrConnection(connection);
                      connection.setEditorialViewStatus("show");
                      cubby.setModal(MODALS.SINGLE_NOSTR_CONNECTION);
                    }}
                  >
                    Get Connection Code
                  </Button>
                </Tooltip>
                {connection.security_level_name === "send_and_receive" && (
                  <Button
                    size="xs"
                    variant="default"
                    leftSection={<IconEdit size={14} />}
                    onClick={() => {
                      cubby.selectNostrConnection(connection);
                      connection.setEditorialViewStatus("edit");
                      cubby.setModal(MODALS.SINGLE_NOSTR_CONNECTION);
                    }}
                  >
                    Edit
                  </Button>
                )}
                {shouldShowDeleteButton() && (
                  <Button
                    size="xs"
                    variant="default"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => setIsConfirmingDelete(true)}
                  >
                    Delete
                  </Button>
                )}
              </Group>
              {connection.security_level_name === "send_and_receive" && (
                <>
                  {hasBudget ? (
                    <Progress.Root size={18} mt="xs" mb="xs" radius="md">
                      <Progress.Section value={budgetUsed} color="#E0E0E0" />
                      <Progress.Section value={100 - budgetUsed} color="blue">
                        <Progress.Label
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "clip",
                            opacity: 0.79,
                          }}
                        >
                          {formatBudget(budgetLeft)} sats remaining
                        </Progress.Label>
                      </Progress.Section>
                    </Progress.Root>
                  ) : (
                    <Alert
                      color="red"
                      title="Send & Receive Without Budget"
                      mt="md"
                    >
                      No budget is set. This connection could quickly spend all
                      your outbound funds.
                    </Alert>
                  )}
                </>
              )}

              <ConnectionStatusBadge connection={connection} />
            </>
          )}

          {isConfirmingDelete && (
            <Stack>
              <Alert color="red" title="Confirm Deletion" mt="md">
                Are you sure you want to delete this? Inbound payments which use
                this connection will fail.
              </Alert>
              <Group gap="xs" mt="sm">
                <Button
                  size="xs"
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={handleDelete}
                >
                  Yes, Delete
                </Button>
                <Button
                  size="xs"
                  color="blue"
                  onClick={() => setIsConfirmingDelete(false)}
                >
                  Cancel
                </Button>
              </Group>
            </Stack>
          )}
          {/* <Text> uuid {connection.uuid}</Text> */}
        </Stack>
      </Card>
    );
  }
);

export default ConnectionCard;
